import { Component, OnInit } from '@angular/core';
import { MatSnackBar } from '@angular/material/snack-bar';
import { ActivatedRoute, Router } from '@angular/router';
import { Store } from '@ngrx/store';
import { Task, TaskStatus } from '@workspaces/models/task';
import { WorkspaceTaskService } from '@workspaces/services/task.service';
import { EditComponent } from '@workspaces/utils/edit-component';
import { AppState } from 'src/app/state/app.state';
import { map, Observable, tap } from 'rxjs';
import { taskListFeature, TaskListFetch } from '@workspaces/store/task-list.feature';
import { CommonModule } from '@angular/common';
import { MatTooltip } from '@angular/material/tooltip';

interface DisplayTask extends Task {
  barLeft: number;
  barWidth: number;
}
@Component({
  selector: 'app-task-timeline',
  templateUrl: './task-timeline.component.html',
  styleUrls: ['./task-timeline.component.scss', '../../global-workspace-styles.scss'],
  standalone: true,
  imports: [CommonModule, MatTooltip]
})
export class TaskTimelineComponent extends EditComponent implements OnInit {
  public sortedTimelineTasks$!: Observable<DisplayTask[]>;
  private timelineStart: Date = new Date();
  private timelineEnd: Date = new Date();
  private totalDurationMs = 0;
  public todayPosition = 0;
  constructor(
    protected override readonly route: ActivatedRoute,
    protected readonly workspaceTaskService: WorkspaceTaskService,
    protected override readonly snackbar: MatSnackBar,
    protected override readonly router: Router,
    private readonly store: Store<AppState>
  ) {
    super(route, snackbar, router);
  }
  override ngOnInit(): void {
    super.ngOnInit();

    this.processTasks();
    this.dispatchInitialActions();
  }

  private processTasks(): void {
    this.sortedTimelineTasks$ = this.store.select(taskListFeature.selectData).pipe(
      tap(cachedTasks => {
        this.calculateTimelineSpan(cachedTasks.tasks);
        this.setTodayPosition();
      }),
      map(cachedTasks => {
        const clonedTasks = structuredClone(cachedTasks.tasks);
        return this.calculateBarPositions(clonedTasks);
      })
    );
  }
  private dispatchInitialActions() {
    this.store.dispatch(TaskListFetch({ workspaceId: this.workspaceId }));
  }

  private calculateTimelineSpan(tasks: Task[]): void {
    const dates = tasks.flatMap(t => [t.created_at, t.due_date].filter(d => d));

    const minDate = new Date(Math.min(...dates.map(date => new Date(date).getTime())));
    const maxDate = new Date(Math.max(...dates.map(date => new Date(date).getTime())));
    this.timelineStart = new Date(minDate.getTime());
    this.timelineEnd = new Date(maxDate.getTime());

    this.totalDurationMs = maxDate.getTime() - minDate.getTime();
  }

  private calculateBarPositions(tasks: Task[]) {
    const updateTasks = tasks.map(task => {
      const startOffsetMs = new Date(task.created_at).getTime() - this.timelineStart.getTime();
      const barLeft = (startOffsetMs / this.totalDurationMs) * 100;
      const taskDurationMs = new Date(task.due_date).getTime() - new Date(task.created_at).getTime();
      const barWidth = (taskDurationMs / this.totalDurationMs) * 100;

      const displayTask: DisplayTask = {
        ...task,
        barLeft: barLeft,
        barWidth: barWidth
      };
      return displayTask;
    });

    updateTasks.sort((a, b) => a.priority - b.priority);
    return updateTasks;
  }

  public getStatusClass(status: TaskStatus): string {
    return `task-bar-${status.replace('_', '-')}`;
  }

  public setTodayPosition() {
    const now = Date.now();
    const timelineStartMs = this.timelineStart.getTime();
    const elapsedDurationMs = now - timelineStartMs;

    let positionPercentage = (elapsedDurationMs / this.totalDurationMs) * 100;

    positionPercentage = Math.max(0, positionPercentage);
    positionPercentage = Math.min(100, positionPercentage);

    return positionPercentage;
  }
}
